import { useCallback, useEffect, useState } from 'react'
import { apiGet, apiPut } from '../../generic/Api_Functions'
import { isSendSettingOn } from '../../generic/hooks/useFabricationOrderClusterSendSetting'

/**
 * Whether the commercial order and its line travel with a sent fabrication order. Independent of
 * the send switch: this one decides how much commercial context goes with an order, not whether
 * orders can be sent at all.
 */
export const ORDER_DATA_SEND_SETTING_ENDPOINT = 'cluster/orderDataSendSetting'

/**
 * Whether the changes made on this node travel to the fabrication orders it already sent elsewhere
 * (historia 108). Third sibling of the family, with the same contract and INDEPENDENT of the other
 * two: turning it off leaves the send of part 1 exactly as it was.
 *
 * ⚠️ RN41″: it governs what this node EMITS. With it off the node keeps RECEIVING and applying what
 * arrives, so it is not "switching synchronisation off".
 */
export const ALIGNMENT_SETTING_ENDPOINT = 'cluster/fabricationOrderAlignmentSetting'

/**
 * Whether the machine states of this node travel to another node (historia 132). Fourth sibling of
 * the family, with the same contract and INDEPENDENT of the other three: what this node reports
 * about its machines has nothing to do with whether it may send fabrication orders.
 *
 * ⚠️ It governs what this node EMITS. With it off the node keeps RECEIVING and applying the states
 * that arrive from elsewhere, so it is not "switching the mirror off".
 */
export const DEVICE_STATUS_SEND_SETTING_ENDPOINT = 'cluster/deviceStatusSendSetting'

/**
 * Whether the production of this node -- its parts and its jobs -- travels to another node
 * (historia 132). Fifth sibling, same contract, independent of the other four.
 *
 * ⚠️ There is ONE business dependency, and it is deliberately NOT drawn on the screen: with the
 * machine states switch off, the per-part job history does not travel even with this one on. That
 * rule lives in the backend; the screen paints two independent switches rather than disabling a row,
 * because a row disabled by another row reads as "this setting is unavailable", which is a lie.
 */
export const PRODUCTION_FEEDBACK_SEND_SETTING_ENDPOINT = 'cluster/productionFeedbackSendSetting'

/**
 * Historia 107 — one plant-cell cluster switch, as the cluster configuration screen drives it.
 *
 * Every switch of this family answers the same contract on a route of its own (`ClusterSendSettingRest`):
 * `GET` needs nothing but an authenticated caller and answers `{ enabled }`; `PUT` takes `{ enabled }`,
 * asks for the `SETTINGS` authority and answers **the state it persisted**; a `400` means the
 * installation has no such setting and **nothing was modified**.
 *
 * Two guarantees the screen leans on:
 *   1. **Default OFF.** Absence, an unreadable answer or a failed read leave the switch off, so an
 *      installation that has not opted in is never shown as if it had.
 *   2. **Never show what was not persisted.** The change is displayed at once so the control does not
 *      feel stuck, but a refusal puts it back where it was: showing it on while the server holds it
 *      off would lie about the state of the installation.
 *
 * @param {string} endpoint Route of this switch
 * @returns {{enabled: boolean, available: boolean, change: function(boolean): Promise<Object>}} Its state
 *   and a writer that resolves to `{ ok, message }`
 */
const useClusterSendSettingControl = (endpoint) => {
  const [enabled, setEnabled] = useState(false)
  // Whether the switch could be read at all. An installation whose database lacks the setting has
  // nothing to write, so offering the control would only buy the user a rejection.
  const [available, setAvailable] = useState(false)

  useEffect(() => {
    let subscribed = true
    const loadSetting = async () => {
      try {
        const setting = await apiGet(endpoint)
        if (!subscribed) {
          return
        }
        setEnabled(isSendSettingOn(setting))
        setAvailable(true)
      } catch (error) {
        // Unreachable is off, and it must never break the screen that asked for it.
        if (!subscribed) {
          return
        }
        setEnabled(false)
        setAvailable(false)
      }
    }
    loadSetting()
    return () => {
      subscribed = false
    }
  }, [endpoint])

  const change = useCallback(async (next) => {
    const previous = enabled
    setEnabled(next)
    try {
      const persisted = await apiPut(endpoint, { enabled: next })
      setEnabled(isSendSettingOn(persisted))
      return { ok: true, message: '' }
    } catch (error) {
      setEnabled(previous)
      // The body of a 400 is `{ code, message }`, and the backend team asks for the `message` here.
      // `Api_Functions` already lifts it into `error.message`.
      return { ok: false, message: error?.message || error?.data?.message || '' }
    }
  }, [enabled, endpoint])

  return { enabled, available, change }
}

export default useClusterSendSettingControl
